<template>
  <b-modal
      id="sales-drill-modal"
      v-model="visible"
      size="xl"
      centered
      hide-header
      hide-footer
      modal-class="sdl-modal"
      body-class="sdl-modal__body"
  >
    <div class="sdl">
      <!-- Hero header -->
      <div class="sdl__hero" :class="heroClass">
        <div class="sdl__hero-icon"><i :class="dimIcon"></i></div>
        <div class="sdl__hero-text">
          <h4 class="sdl__hero-title">{{ label || $t('salesDashboard.segment') }}</h4>
          <span class="sdl__hero-sub">{{ dimLabel }}</span>
        </div>
        <button type="button" class="sdl__close" @click="visible = false" aria-label="Close">
          <i class="fa-solid fa-xmark"></i>
        </button>
      </div>

      <!-- Tabs -->
      <div class="sdl__tabs">
        <button type="button" class="sdl__tab" :class="{ 'is-active': activeTab === 'DEPOSITS' }"
                @click="switchTab('DEPOSITS')">
          <i class="fa-solid fa-money-bill-trend-up mr-50"></i>{{ $t('salesDashboard.depositsTab') }}
          <span class="sdl__tab-count" v-if="deposits.loaded">{{ deposits.total }}</span>
        </button>
        <button type="button" class="sdl__tab" :class="{ 'is-active': activeTab === 'CLIENTS' }"
                @click="switchTab('CLIENTS')">
          <i class="fa-solid fa-users mr-50"></i>{{ $t('salesDashboard.clientsTab') }}
          <span class="sdl__tab-count" v-if="clients.loaded">{{ clients.total }}</span>
        </button>
      </div>

      <!-- DEPOSITS tab -->
      <b-overlay v-if="activeTab === 'DEPOSITS'" :show="deposits.loading" rounded opacity="0.4" spinner-variant="primary">
        <div class="sdl__table-wrap">
          <b-table responsive hover :items="deposits.items" :fields="depositFields" show-empty
                   class="sdl-table mb-0" @row-clicked="openClient">
            <template #cell(clientName)="data">
              <span class="sdl-name">{{ data.value || $t('salesDashboard.unknownClient') }}</span>
              <b-badge v-if="data.item.secondAgentName" pill variant="light-warning" class="ml-25"
                       v-b-tooltip.hover :title="$t('salesDashboard.twoAgents', { agent: data.item.secondAgentName })">
                <i class="fa-solid fa-user-group"></i> 2
              </b-badge>
            </template>
            <template #cell(amount)="data">
              <span class="font-weight-bold" :class="data.item.type === 'WITHDRAWAL' ? 'text-danger' : 'text-success'">
                {{ convertToCurrency(data.value, data.item.currency) }}
              </span>
            </template>
            <template #cell(type)="data">
              <b-badge pill :variant="data.value === 'WITHDRAWAL' ? 'light-danger' : 'light-success'">
                {{ data.value }}
              </b-badge>
            </template>
            <template #cell(flags)="data">
              <b-badge v-if="data.item.isFtd" pill variant="light-primary" class="mr-25">FTD</b-badge>
              <b-badge v-if="data.item.isStd" pill variant="light-info">STD</b-badge>
              <span v-if="!data.item.isFtd && !data.item.isStd" class="text-muted">—</span>
            </template>
            <template #cell(country)="data"><span>{{ data.value || '—' }}</span></template>
            <template #cell(agentName)="data"><span>{{ data.value || '—' }}</span></template>
            <template #cell(approvedByName)="data"><span>{{ data.value || '—' }}</span></template>
            <template #cell(registrationTime)="data">
              <span class="text-nowrap">{{ data.value | momentDateTime }}</span>
            </template>
            <template #cell(open)="data">
              <b-button size="sm" variant="flat-primary" class="btn-icon" @click.stop="openClient(data.item)"
                        v-b-tooltip.hover :title="$t('clientDashboard.openClient')">
                <i class="fa-solid fa-arrow-up-right-from-square"></i>
              </b-button>
            </template>
            <template #empty>
              <div class="text-center text-muted py-3">{{ $t('salesDashboard.noData') }}</div>
            </template>
          </b-table>
        </div>
        <div class="sdl__footer" v-if="deposits.total > 0">
          <b-pagination v-model="deposits.page" :total-rows="deposits.total" :per-page="deposits.size"
                        size="sm" class="mb-0" @change="onPageChange('DEPOSITS', $event)"/>
          <div class="sdl__per-page">
            <b-form-select v-model="deposits.size" size="sm" :options="[25, 50, 100]"
                           @change="onSizeChange('DEPOSITS')" style="width: 80px"/>
            <span class="text-muted ml-50">/ {{ deposits.total }}</span>
          </div>
        </div>
      </b-overlay>

      <!-- CLIENTS tab -->
      <b-overlay v-else :show="clients.loading" rounded opacity="0.4" spinner-variant="primary">
        <div class="sdl__table-wrap">
          <b-table responsive hover :items="clients.items" :fields="clientFields" show-empty
                   class="sdl-table mb-0" @row-clicked="openClient">
            <template #cell(clientName)="data">
              <span class="sdl-name">{{ data.value || $t('salesDashboard.unknownClient') }}</span>
              <b-badge v-if="data.item.secondAgentName" pill variant="light-warning" class="ml-25"
                       v-b-tooltip.hover :title="$t('salesDashboard.twoAgents', { agent: data.item.secondAgentName })">
                <i class="fa-solid fa-user-group"></i> 2
              </b-badge>
            </template>
            <template #cell(email)="data"><span class="sdl-ellipsis">{{ data.value || '—' }}</span></template>
            <template #cell(country)="data"><span>{{ data.value || '—' }}</span></template>
            <template #cell(totalDeposit)="data">
              <span v-if="data.value != null" class="font-weight-bold text-success">{{ convertToCurrency(data.value, data.item.currency) }}</span>
              <span v-else class="text-muted">—</span>
            </template>
            <template #cell(depositCount)="data">
              <span v-if="data.value != null">{{ data.value }}</span>
              <span v-else class="text-muted">—</span>
            </template>
            <template #cell(depositorName)="data"><span>{{ data.value || '—' }}</span></template>
            <template #cell(approvedByName)="data"><span>{{ data.value || '—' }}</span></template>
            <template #cell(open)="data">
              <b-button size="sm" variant="flat-primary" class="btn-icon" @click.stop="openClient(data.item)"
                        v-b-tooltip.hover :title="$t('clientDashboard.openClient')">
                <i class="fa-solid fa-arrow-up-right-from-square"></i>
              </b-button>
            </template>
            <template #empty>
              <div class="text-center text-muted py-3">{{ $t('salesDashboard.noData') }}</div>
            </template>
          </b-table>
        </div>
        <div class="sdl__footer" v-if="clients.total > 0">
          <b-pagination v-model="clients.page" :total-rows="clients.total" :per-page="clients.size"
                        size="sm" class="mb-0" @change="onPageChange('CLIENTS', $event)"/>
          <div class="sdl__per-page">
            <b-form-select v-model="clients.size" size="sm" :options="[25, 50, 100]"
                           @change="onSizeChange('CLIENTS')" style="width: 80px"/>
            <span class="text-muted ml-50">/ {{ clients.total }}</span>
          </div>
        </div>
      </b-overlay>
    </div>
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex';
import mixinBase from '@/components/mixin/mixinBase';
import mixinNotifications from '@/components/mixin/mixinNotifications';

const DIM_ICON = {
  AGENT: 'fa-solid fa-user-tie',
  DEPOSIT_COUNTRY: 'fa-solid fa-earth-americas',
  CLIENT_STATUS: 'fa-solid fa-flag',
  CLIENT_COUNTRY: 'fa-solid fa-map-location-dot',
};

const blankTab = () => ({ items: [], total: 0, page: 1, size: 25, loaded: false, loading: false });

export default {
  name: 'SalesDrillModal',
  mixins: [mixinBase, mixinNotifications],
  data: () => ({
    visible: false,
    dimension: 'AGENT',
    value: null,
    label: '',
    activeTab: 'DEPOSITS',
    ctx: {},
    deposits: blankTab(),
    clients: blankTab(),
  }),
  computed: {
    dimIcon() {
      return DIM_ICON[this.dimension] || 'fa-solid fa-chart-simple';
    },
    heroClass() {
      return 'sdl__hero--' + (this.dimension || 'AGENT').toLowerCase();
    },
    dimLabel() {
      return this.$t('salesDashboard.dim.' + this.dimension);
    },
    depositFields() {
      return [
        { key: 'transactionId', label: this.$t('columns.id'), class: 'text-nowrap' },
        { key: 'clientName', label: this.$t('salesDashboard.client') },
        { key: 'amount', label: this.$t('columns.amount') },
        { key: 'type', label: this.$t('columns.type') },
        { key: 'flags', label: this.$t('columns.ftdStd') },
        { key: 'country', label: this.$t('columns.country') },
        { key: 'agentName', label: this.$t('columns.depositorUserId') },
        { key: 'approvedByName', label: this.$t('columns.statusUserId') },
        { key: 'registrationTime', label: this.$t('columns.registrationTime'), class: 'text-nowrap' },
        { key: 'open', label: '', class: 'text-right' },
      ];
    },
    clientFields() {
      return [
        { key: 'clientName', label: this.$t('salesDashboard.client') },
        { key: 'email', label: this.$t('columns.email') },
        { key: 'country', label: this.$t('columns.country') },
        { key: 'totalDeposit', label: this.$t('salesDashboard.totalDeposited') },
        { key: 'depositCount', label: this.$t('salesDashboard.depositsCount') },
        { key: 'depositorName', label: this.$t('columns.depositorUserId') },
        { key: 'approvedByName', label: this.$t('columns.statusUserId') },
        { key: 'open', label: '', class: 'text-right' },
      ];
    },
  },
  methods: {
    ...mapActions('dashboard', ['getSalesDrillList']),
    /**
     * @param {Object} p { dimension, value, label, ctx:{orgIds,buIds,buTypes,affiliateId,banner,campaign,realOnly,range} }
     */
    open(p) {
      this.dimension = p.dimension || 'AGENT';
      this.value = p.value != null ? p.value : null;
      this.label = p.label || '';
      this.ctx = p.ctx || {};
      this.deposits = blankTab();
      this.clients = blankTab();
      // Deposit dimensions open on Deposits; client dimensions on Clients.
      this.activeTab = (this.dimension === 'CLIENT_STATUS' || this.dimension === 'CLIENT_COUNTRY'
          || this.dimension === 'DUPLICATED')
          ? 'CLIENTS' : 'DEPOSITS';
      this.visible = true;
      this.fetch(this.activeTab);
    },
    switchTab(tab) {
      if (this.activeTab === tab) return;
      this.activeTab = tab;
      const bucket = tab === 'DEPOSITS' ? this.deposits : this.clients;
      if (!bucket.loaded && !bucket.loading) {
        this.fetch(tab);
      }
    },
    fetch(tab) {
      const bucket = tab === 'DEPOSITS' ? this.deposits : this.clients;
      bucket.loading = true;
      this.getSalesDrillList({
        ...this.ctx,
        dimension: this.dimension,
        value: this.value,
        tab,
        pagination: { page: bucket.page, size: bucket.size },
      })
        .then(
          res => {
            bucket.items = Array.isArray(res.data) ? res.data : [];
            bucket.total = res.metadata && res.metadata.total != null ? res.metadata.total : bucket.items.length;
            bucket.loaded = true;
          },
          err => this.showServerErrorNotification('fetchDataError', 'salesDashboard', err)
        )
        .finally(() => { bucket.loading = false; });
    },
    onPageChange(tab, p) {
      const bucket = tab === 'DEPOSITS' ? this.deposits : this.clients;
      bucket.page = p;
      this.$nextTick(() => this.fetch(tab));
    },
    onSizeChange(tab) {
      const bucket = tab === 'DEPOSITS' ? this.deposits : this.clients;
      bucket.page = 1;
      this.$nextTick(() => this.fetch(tab));
    },
    openClient(item) {
      if (!item || !item.clientId) return;
      const route = this.$router.resolve({ name: 'client', params: { clientId: item.clientId } });
      window.open(route.href, '_blank');
    },
  },
};
</script>

<style lang="scss">
.sdl-modal {
  .modal-content { border: none; border-radius: 18px; overflow: hidden; box-shadow: 0 24px 60px -20px rgba(20, 24, 50, 0.45); }
  .modal-body.sdl-modal__body { padding: 0; }
}

.sdl {
  display: flex;
  flex-direction: column;

  &__hero {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1.25rem 1.5rem;
    color: #fff;
    background: linear-gradient(135deg, #4da3ff 0%, #007BFF 55%, #0062cc 100%);

    &--deposit_country { background: linear-gradient(135deg, #34d399 0%, #10b981 55%, #059669 100%); }
    &--client_status { background: linear-gradient(135deg, #a78bfa 0%, #7c3aed 55%, #5b21b6 100%); }
    &--client_country { background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 55%, #d97706 100%); }
  }
  &__hero-icon {
    display: flex; align-items: center; justify-content: center;
    width: 48px; height: 48px; border-radius: 13px; font-size: 1.3rem;
    background: rgba(255, 255, 255, 0.22); flex-shrink: 0;
  }
  &__hero-text { flex: 1; min-width: 0; }
  &__hero-title { margin: 0; font-weight: 700; font-size: 1.2rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  &__hero-sub { font-size: 0.82rem; opacity: 0.9; text-transform: uppercase; letter-spacing: 0.04em; }
  &__close {
    border: none; background: rgba(255, 255, 255, 0.18); color: #fff;
    width: 34px; height: 34px; border-radius: 10px; font-size: 1rem; cursor: pointer;
    transition: background 0.2s ease, transform 0.2s ease; flex-shrink: 0;
    &:hover { background: rgba(255, 255, 255, 0.32); transform: rotate(90deg); }
  }

  &__tabs {
    display: flex;
    gap: 0.4rem;
    padding: 0.6rem 1.25rem 0;
    border-bottom: 1px solid rgba(110, 117, 159, 0.12);
  }
  &__tab {
    border: none;
    background: transparent;
    color: #6e6b7b;
    font-weight: 600;
    font-size: 0.85rem;
    padding: 0.6rem 0.9rem;
    border-radius: 10px 10px 0 0;
    cursor: pointer;
    border-bottom: 2px solid transparent;
    transition: all 0.18s ease;
    &:hover { color: #007BFF; background: rgba(0, 123, 255, 0.06); }
    &.is-active { color: #007BFF; border-bottom-color: #007BFF; }
  }
  &__tab-count {
    display: inline-block;
    margin-left: 0.4rem;
    padding: 0.05rem 0.45rem;
    border-radius: 999px;
    background: rgba(0, 123, 255, 0.12);
    font-size: 0.7rem;
  }

  &__table-wrap { max-height: 58vh; overflow-y: auto; }

  &__footer {
    display: flex; align-items: center; justify-content: space-between;
    padding: 0.85rem 1.25rem; border-top: 1px solid rgba(110, 117, 159, 0.12);
  }
  &__per-page { display: flex; align-items: center; }
}

.sdl-table {
  font-size: 0.85rem;
  thead th {
    background: #f7f9fc; color: #6e6b7b; text-transform: uppercase;
    font-size: 0.7rem; letter-spacing: 0.04em;
  }
  tbody tr { cursor: pointer; }
  .sdl-name { font-weight: 600; }
  .sdl-ellipsis {
    display: inline-block; max-width: 220px; overflow: hidden;
    text-overflow: ellipsis; white-space: nowrap; vertical-align: bottom;
  }
}

/* Dark mode */
body.dark-layout {
  .sdl-modal .modal-content { background: var(--crm-dark-surface, #232c3e); color: #d0d2d6; }
  .sdl__tabs { border-bottom-color: rgba(255, 255, 255, 0.08); }
  .sdl__tab { color: #8b93ab; &:hover { background: rgba(123, 175, 255, 0.10); color: #a8b6ff; } &.is-active { color: #a8b6ff; border-bottom-color: #a8b6ff; } }
  .sdl__tab-count { background: rgba(123, 175, 255, 0.16); }
  .sdl__footer { border-top-color: rgba(255, 255, 255, 0.08); }
  .sdl-table thead th { background: var(--crm-dark-surface-2, #2e3850); color: #8b93ab; }
}
</style>
